/**
 * Turning a blueprint into an actual booth.
 *
 * ## The problem this solves
 *
 * Picking "Nightclub" used to write one string — `blueprint_slug` — and change
 * nothing else. The booth kept whatever modes, participant step and snapshot
 * settings it already had. The Experience tab's own description said it "sets
 * the language, the flow, and what extra details guests are asked for", and two
 * thirds of that was untrue.
 *
 * So an operator picked the format that matches their event, saw the wording
 * change, reasonably assumed the rest had followed, and opened a nightclub booth
 * that still offered a 40-second AI generation to a queue at 1am.
 *
 * A blueprint now carries `defaults` describing what the booth should *be*, and
 * this module applies them.
 *
 * ## Why it writes to two places
 *
 * Service flags (`ai_photo_booth`, `photo_booth`, `print_photo`) are columns on
 * the event record, not keys in the config JSON. Everything else — workflow,
 * snapshot, prompts, QR — is config. So applying a blueprint is one PATCH to the
 * event and one config save. `services` is deliberately stripped out of the
 * config patch: it isn't in the schema, and leaving it in would fail validation
 * on every save afterwards.
 */

import type { BlueprintDetail, EventDetail } from "@/lib/types/api";
import type { DraftConfig } from "@/lib/types/config";

/** Event-record fields a blueprint can set. */
export interface ServiceFlags {
  ai_photo_booth?: boolean;
  photo_booth?: boolean;
  print_photo?: boolean;
}

export interface BlueprintPlan {
  /** PATCH to the event record. Empty when nothing would change. */
  record: ServiceFlags;
  /** Merge into the draft config. Empty when nothing would change. */
  config: DraftConfig;
  /** Plain-English list of what will change, for the confirmation. */
  changes: string[];
}

const SERVICE_LABELS: Record<keyof ServiceFlags, string> = {
  ai_photo_booth: "AI photo booth",
  photo_booth: "Photo booth",
  print_photo: "Printing",
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Deep merge, with arrays replacing rather than concatenating. */
function merge<T extends Record<string, unknown>>(base: T, patch: Record<string, unknown>): T {
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    out[key] = isObject(value) && isObject(out[key])
      ? merge(out[key] as Record<string, unknown>, value)
      : value;
  }
  return out as T;
}

/**
 * Works out what applying `blueprint` would do, without doing it.
 *
 * Nothing is applied until the operator accepts. Switching blueprint on a
 * configured event is destructive — it can turn off a mode they rely on or add
 * a screen they removed — and doing that on a single click, silently, is how
 * someone loses an afternoon's setup during an event.
 */
export function planBlueprint(
  blueprint: BlueprintDetail,
  event: EventDetail,
  draft: DraftConfig,
): BlueprintPlan {
  const defaults = (blueprint.defaults ?? {}) as Record<string, unknown>;
  const changes: string[] = [];

  // ── Event record: which booth tools are on ──────────────────────────────
  const record: ServiceFlags = {};
  const services = isObject(defaults.services) ? defaults.services : {};

  (Object.keys(SERVICE_LABELS) as Array<keyof ServiceFlags>).forEach((key) => {
    if (!(key in services)) return;
    const next = Boolean(services[key]);
    if (Boolean(event[key]) === next) return;

    record[key] = next;
    changes.push(`${next ? "Turn on" : "Turn off"} ${SERVICE_LABELS[key]}`);
  });

  // ── Config: everything that shapes the guest's journey ──────────────────
  // `services` is removed here: it's a record concern, and it isn't in the
  // config schema, so leaving it would make every later save invalid.
  const { services: _ignored, ...configDefaults } = defaults;
  const config = configDefaults as DraftConfig;

  const skip = (configDefaults as DraftConfig).workflow?.skip_participants;
  if (skip !== undefined && Boolean(draft.workflow?.skip_participants) !== skip) {
    changes.push(skip ? "Remove the participant selection screen" : "Add a participant selection screen");
  }

  const snapshot = (configDefaults as DraftConfig).snapshot;
  if (snapshot?.filters && (draft.snapshot?.filters ?? []).join() !== snapshot.filters.join()) {
    changes.push(`Set snapshot filters to ${snapshot.filters.join(", ")}`);
  }
  if (snapshot?.free_retakes !== undefined && draft.snapshot?.free_retakes !== snapshot.free_retakes) {
    changes.push(`Set free retakes to ${snapshot.free_retakes}`);
  }

  const prompt = (configDefaults as DraftConfig).event?.system_prompt;
  if (prompt && draft.event?.system_prompt !== prompt) {
    changes.push("Replace the AI house style prompt");
  }

  // The vocabulary always changes — that's the visible part, and the reason
  // someone picked a blueprint at all. Worth naming so the list isn't empty
  // when a blueprint's defaults happen to match the current setup.
  changes.push(`Rename booth wording to ${blueprint.name}`);

  return { record, config, changes };
}

/** Applies the config half of a plan to a draft. */
export function applyBlueprintConfig(
  draft: DraftConfig,
  blueprint: BlueprintDetail,
  plan: BlueprintPlan,
): DraftConfig {
  const merged = merge(draft as Record<string, unknown>, plan.config as Record<string, unknown>);

  return {
    ...(merged as DraftConfig),
    experience: {
      // Switching format clears the previous format's word overrides. Keeping
      // them would leave a nightclub booth still saying "Choose your club",
      // which is the exact confusion blueprints exist to prevent.
      taxonomy: {},
      blueprint_slug: blueprint.slug,
    },
  };
}
